/**
 * The first things a new church sets up, in the order that makes each next
 * step easier: people before groups, rooms before Sunday, a fund before the
 * giving page goes out.
 *
 * Each step is "done" by what is in the database, not by a box someone
 * ticked — import 300 people and "Bring in your people" is done, whichever
 * screen they came through. The API reports which are done; the screen shows
 * what is left, to whoever can do it.
 */
export const ONBOARDING_STEPS = [
  {
    key: 'people',
    title: 'Bring in your people',
    detail:
      'Import a spreadsheet from Planning Center, Breeze or anywhere else, or add people one by one.',
    to: '/people/import',
    permission: 'people:write',
  },
  {
    key: 'team',
    title: 'Invite your team',
    detail: 'Staff and volunteer leaders each sign in as themselves, with a role that fits.',
    to: '/settings/invitations',
    permission: 'org:admin',
  },
  {
    key: 'groups',
    title: 'Set up your groups',
    detail: 'Small groups, classes and teams — leaders take attendance from their phones.',
    to: '/groups',
    permission: 'groups:write',
  },
  {
    key: 'checkin',
    title: 'Get check-in ready for Sunday',
    detail: 'Add your children’s rooms, then register a tablet as a kiosk.',
    to: '/checkin',
    permission: 'checkin:admin',
  },
  {
    key: 'giving',
    title: 'Open online giving',
    detail: 'Create a fund; your giving page is ready to share.',
    to: '/giving',
    permission: 'giving:admin',
  },
  {
    key: 'messaging',
    title: 'Choose who your messages come from',
    detail: 'The name people see on your emails, and a number for texts.',
    to: '/messages',
    permission: 'messages:admin',
  },
] as const

export type OnboardingStepKey = (typeof ONBOARDING_STEPS)[number]['key']

/** "People" counts as done from this many records — a handful typed in to try it out is not a directory. */
export const ONBOARDING_PEOPLE_THRESHOLD = 10
